import {IOrder} from "../models/IOrder.ts";
import {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import AuthContext from "./AuthContext.tsx";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api'

const defaultOrder: IOrder = {
    items: [],
    shippingAddress: {
        name: '',
        street: '',
        address2: '',
        city: '',
        region: '',
        postalCode: '',
        country: '',
        email: '',
    },
    paymentMethod: 'Kreditkarte',
    totalPrice: 0,
    shippingCost: 0,
}

interface IOrderContextProps {
    orders?: IOrder[]
    order: IOrder
    errorFetchingOrder: Error | null
    setOrder: (order: IOrder) => void
    setOrderId: (orderId: string | undefined) => void
    updateOrderId: (orderId: string) => void
    createOrderMutation: any
    errorCreatingOrder: boolean
    updateOrderMutation: any
    errorUpdatingOrder: any
    finishCheckoutMutation: any
    errorFinishCheckout: any
    addTrackingIdMutation: any
    sendConfirmationMail: any
    sendShippingConfirmationMail: any
    selectedCurrency: string
    setSelectedCurrency: (selectedCurrency: string) => void
    rates: { [key: string]: number }
}

const OrderContext = createContext<IOrderContextProps>({} as IOrderContextProps)

interface IOrderProviderProps {
    children: ReactNode
}

export const OrderProvider = ({children}: IOrderProviderProps) => {
    const {isLoggedIn, isAdmin} = useContext(AuthContext)
    const [errorCreatingOrder, setErrorCreatingOrder] = useState<boolean>(false)
    const [errorUpdatingOrder, setErrorUpdatingOrder] = useState<boolean>(false)
    const [errorFinishCheckout, setErrorFinishCheckout] = useState<boolean>(false)
    const [order, setOrder] = useState<IOrder>(defaultOrder)
    const [orderId, setOrderId] = useState<string | undefined>()
    const queryClient = useQueryClient()
    const [selectedCurrency, setSelectedCurrency] = useState<string>("EUR")
    const [rates, setRates] = useState<{ [key: string]: number }>({EUR: 1});

    useEffect(() => {
        const fetchRates = async () => {
            try {
                const response = await fetch('https://open.er-api.com/v6/latest/EUR');
                const data = await response.json();
                setRates(data.rates);
                const saved = localStorage.getItem('currency');
                const initialCurrency = saved ? saved : 'EUR';
                setSelectedCurrency(initialCurrency);
            } catch (err) {
                console.error('Failed to fetch exchange rates', err);
            }
        };

        fetchRates();
    }, []);

    const {data: orders} = useQuery<IOrder[]>({
        queryKey: ["orders", isAdmin ? isAdmin : ""],
        enabled: isLoggedIn,
        queryFn: async () => {
            const response = isAdmin
                ? await axios.get(`${VITE_API_BASE_URL}/orders/admin`, {withCredentials: true})
                : await axios.get(`${VITE_API_BASE_URL}/orders`, {withCredentials: true})
            return response.data
        }
    })

    useEffect(() => {
        queryClient.invalidateQueries({queryKey: ["orders"]})
    }, [isAdmin, isLoggedIn])

    const {data: fetchedOrder, error: errorFetchingOrder} = useQuery<IOrder>({
        queryKey: ['order', orderId],
        retry: false,
        queryFn: async () => {
            if (!orderId) return defaultOrder; // Reset to default order when orderId is unset

            const response = isLoggedIn
                ? await axios.get(`${VITE_API_BASE_URL}/orders/auth/${orderId}`, {withCredentials: true})
                : await axios.get(`${VITE_API_BASE_URL}/orders/${orderId}`, {withCredentials: true});

            return response.status === 200 ? response.data : defaultOrder;
        },
    });

    useEffect(() => {
        if (fetchedOrder) {
            setOrder(fetchedOrder);
        }
    }, [fetchedOrder])

    const updateOrderId = (orderId: string) => {
        setOrderId(orderId)
    }

    const createOrderMutation = useMutation({
        mutationFn: async (order: IOrder) => {
            let response
            if (isLoggedIn) {
                response = await axios.post(`${VITE_API_BASE_URL}/orders/auth`, order, {
                    withCredentials: true,
                })
            } else {
                response = await axios.post(`${VITE_API_BASE_URL}/orders`, order, {
                    withCredentials: true
                })
            }
            if (response.status === 201) {
                setOrderId(response.data._id)
                return response.data
            }
        },
        onSuccess: () => {
            setErrorCreatingOrder(false)
            queryClient.invalidateQueries({queryKey: ['order', orderId]})
            queryClient.invalidateQueries({queryKey: ["orders"]})
        },
        onError: () => {
            setErrorCreatingOrder(true)
        }
    })

    const updateOrderMutation = useMutation({
        mutationFn: async (order: IOrder) => {
            let response
            if (isLoggedIn) {
                response = await axios.put(`${VITE_API_BASE_URL}/orders/auth/${order._id}`, order, {
                    withCredentials: true,
                })
            } else {
                response = await axios.put(`${VITE_API_BASE_URL}/orders/${order._id}`, order, {
                    withCredentials: true,
                })
            }
            return response.status === 200 ? response.data : response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['order', orderId]})
            queryClient.invalidateQueries({queryKey: ["orders"]})
        },
        onError: () => {
            setErrorUpdatingOrder(true)
        }
    })

    const finishCheckoutMutation = useMutation({
        mutationFn: async (orderId: string) => {
            if (isLoggedIn) {
                await axios.put(`${VITE_API_BASE_URL}/orders/auth/finishCheckout/${orderId}`, {}, {
                    withCredentials: true
                })
            } else {
                await axios.put(`${VITE_API_BASE_URL}/orders/finishCheckout/${orderId}`, {}, {
                    withCredentials: true
                })
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['cart']})
            queryClient.invalidateQueries({queryKey: ['products']})
        },
        onError: () => {
            setErrorFinishCheckout(true)
        }
    })

    const addTrackingIdMutation = useMutation({
        mutationFn: async (updateInformation: { orderId?: string, trackingId?: string }) => {
            const response = await axios.put(`${VITE_API_BASE_URL}/orders/addTrackingId/${updateInformation.orderId}`, {trackingId: updateInformation.trackingId}, {withCredentials: true})
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["orders"]})
            queryClient.invalidateQueries({queryKey: ['order', orderId]})
        }
    })

    const sendConfirmationMail = async (orderId: string, language: string, email?: string,) => {
        if (isLoggedIn) {
            await axios.post(`${VITE_API_BASE_URL}/email/send-order-email/auth/${orderId}`, {language: language}, {
                withCredentials: true
            })
        } else {
            await axios.post(`${VITE_API_BASE_URL}/email/send-order-email/${orderId}`, {
                email: email,
                language: language
            }, {
                withCredentials: true
            })
        }
    }

    const sendShippingConfirmationMail = async (trackingId: string, orderId: string, language: string) => {
        await axios.post(`${VITE_API_BASE_URL}/email/send-shipping-confirmation`,
            {
                trackingId: trackingId,
                orderId: orderId,
                language: language
            },
            {
                withCredentials: true
            }
        )
    }

    return (
        <OrderContext.Provider
            value={{
                orders,
                order,
                errorFetchingOrder,
                setOrder,
                setOrderId,
                updateOrderId,
                createOrderMutation,
                errorCreatingOrder,
                updateOrderMutation,
                errorUpdatingOrder,
                finishCheckoutMutation,
                errorFinishCheckout,
                addTrackingIdMutation,
                sendShippingConfirmationMail,
                sendConfirmationMail,
                selectedCurrency,
                setSelectedCurrency,
                rates
            }}>
            {children}
        </OrderContext.Provider>
    )
}

export default OrderContext